import React, { useEffect, useState } from "react";

import {
  Typography,
  WorkerIcon as Worker,
  AutoscalingIcon
} from "@packages/ds";
import { FormattedMessage, useIntl } from "react-intl";

import ServiceIcon from "Components/ServiceIcon";
import { Button } from "ds/Button";
import Dialog from "ds/Dialog";
import Select from "ds/Select";
import { Tooltip } from "ds/Tooltip";
import useDecodedParams from "Hooks/useDecodedParams";
import useMediaQuery from "Hooks/useMediaQuery";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { nextDeploymentSelector } from "Reducers/environment/deployment";
import { getAutoscalingConfigurationThunk } from "Reducers/environment/service/service.slice";
import { useAutoscalingConfigLink } from "src/organization/pages/project/environment/ConfigureAutoscaling/useAutoscalingConfigLink";
import { useAutoscallingValues } from "src/organization/pages/project/environment/metrics/containers/Service/util";
import { useAppDispatch } from "Store/hooks";

import * as S from "./ResourceRow.style";

import type { ResourceData } from "src/organization/pages/project/environment/ConfigureResourcesPage/types";

type Option = {
  value: string;
  label: string;
  disabled?: boolean;
};

type TypeOption = {
  value: string;
  label: string;
};

interface ResourceRowProps {
  formStateRow: ResourceData;
  isWorker: boolean;
  formIsDisabled: boolean;
  isHigherMemoryProfile: boolean;
  // TYPE
  typeSelection: TypeOption;
  typeOnChange: (typeOption: TypeOption) => void;
  // CPU & RAM
  cpuRamValue: Option;
  cpuRamOptions: Array<{ label: string; options: Option[] }>;
  cpuRamOnChange: (option) => void;
  cpuRamError?: boolean;
  // Instance
  instancesValue: Option;
  instancesOptions: Option[];
  instancesOnChange: (option: Option) => void;
  instancesOnlyText: string;
  // Disk
  diskValue: string | null;
  onDiskBlur: (newNumber: string, oldNumber: number | null) => void;
  diskOnChange: (newNumber: string, oldNumber: number | null) => void;
  diskOnlyText: string;
  diskError: boolean;
  diskTooltip: string;
  showTooltip: boolean;
}
const ResourceRow = ({
  formStateRow,
  isWorker,
  formIsDisabled,
  isHigherMemoryProfile,
  // TYPE
  typeSelection,
  typeOnChange,
  // CPU & RAM
  cpuRamValue,
  cpuRamOptions,
  cpuRamOnChange,
  cpuRamError,
  // Instance
  instancesValue,
  instancesOptions,
  instancesOnChange,
  instancesOnlyText,
  // Disk
  diskValue,
  onDiskBlur,
  diskOnChange,
  diskOnlyText,
  diskError,
  diskTooltip,
  showTooltip
}: ResourceRowProps) => {
  const intl = useIntl();
  const [isTooltipOpen, setIsTooltipOpen] = useState<boolean>(false);
  const lessThan720 = useMediaQuery(`(max-width: 720px)`);
  const { projectId, environmentId } = useDecodedParams();

  const nextDeployment = useSelectorWithUrlParams(nextDeploymentSelector);

  const { label, resourceType, typeOptions, type } = formStateRow;

  const service = nextDeployment?.[resourceType]?.[label];

  const { autoscalingEnabledFlag, handleConfigureAutoscaling } =
    useAutoscalingConfigLink(service);
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(getAutoscalingConfigurationThunk({ projectId, environmentId }));
  }, [dispatch, projectId, environmentId]);

  const { thresholdsEnabled, hasCapability } = useAutoscallingValues(
    service?.name
  );

  const typeTooltip = intl.formatMessage({
    id: "configure_resources.page.type.tooltip",
    defaultMessage:
      "Guaranteed resources are not available for the Higher memory profile."
  });

  const typeSelect = (
    <S.SelectWrapper>
      <S.MobileLabel textStyle="default">
        {intl.formatMessage({
          id: "configure_resources.page.type",
          defaultMessage: "Type"
        })}
      </S.MobileLabel>
      <Select
        placeholder={
          <S.PlaceholderText weight="regular" size="medium">
            {intl.formatMessage({
              id: "configure_resources.page.type.placeholder",
              defaultMessage: "Select type"
            })}
          </S.PlaceholderText>
        }
        id="configure_resources.page.type.placeholder"
        value={typeSelection.label ? typeSelection : undefined}
        options={typeOptions}
        onChange={typeSelection => {
          typeOnChange(typeSelection as TypeOption);
        }}
        isDisabled={formIsDisabled || isHigherMemoryProfile}
      />
    </S.SelectWrapper>
  );

  const typeSelectComponent = isHigherMemoryProfile ? (
    <Tooltip
      tooltip={typeTooltip}
      side="top"
      align="center"
      analyticId="type_tooltip"
    >
      {typeSelect}
    </Tooltip>
  ) : (
    typeSelect
  );

  const showDiskPlaceholder = diskValue === null;

  const diskLabel = intl.formatMessage({
    id: `configure_resources.page.disk_n_storage`,
    defaultMessage: "Disk / storage"
  });

  const cleanDiskValue =
    diskValue != null ? parseFloat((+diskValue / 1024).toFixed(3)) : null;

  const readyDiskValue = cleanDiskValue ? `${cleanDiskValue}` : "";

  const invalidChars = ["e", "E", "+", "-"];

  const diskInput = (
    <S.InputWrapper>
      <S.Input
        placeholder={
          showDiskPlaceholder
            ? intl.formatMessage({
                id: "configure_resources.page.disk.placeholder",
                defaultMessage: "ex 512 MB"
              })
            : ""
        }
        onMouseEnter={() => {
          if (showTooltip) {
            setIsTooltipOpen(true);
          }
        }}
        onMouseLeave={() => {
          if (showTooltip) {
            setIsTooltipOpen(false);
          }
        }}
        id={`${type}-${label}-disk`}
        label={lessThan720 ? diskLabel : undefined}
        value={readyDiskValue ?? ""}
        disabled={formIsDisabled}
        isValid={true}
        onBlur={e => {
          const value = +e.currentTarget.value;

          const roundedToNearestMb = Math.round(value * 1024);
          onDiskBlur(`${roundedToNearestMb}`, cleanDiskValue);
        }}
        onChange={e => {
          const value = +e.currentTarget.value;

          diskOnChange(`${value * 1024}`, cleanDiskValue);
        }}
        onKeyDown={evt => {
          invalidChars.includes(evt.key) && evt.preventDefault();
        }}
        onPaste={evt => {
          invalidChars.includes(evt.clipboardData.getData("Text")) &&
            evt.preventDefault();
        }}
        width="100%"
        type="number"
        autoComplete="off"
        step="0.10"
      />
      {!showDiskPlaceholder && (
        <S.Blank gapSpace={readyDiskValue.length * 9} disabled={formIsDisabled}>
          {intl.formatMessage({
            id: "configure_resources.page.disk.units.label",
            defaultMessage: "GB"
          })}
        </S.Blank>
      )}
      {diskError && (
        <S.ErrorText>
          {intl.formatMessage({
            id: `configure_resources.page.disk.error`,
            defaultMessage: "Disk is required"
          })}
        </S.ErrorText>
      )}
    </S.InputWrapper>
  );

  // Show the tooltip only when the disk value hasn't been set/saved
  const diskComponent = showTooltip ? (
    <Tooltip
      tooltip={diskTooltip}
      side="bottom"
      align="end"
      open={isTooltipOpen}
      analyticId="disk_tooltip"
    >
      {diskInput}
    </Tooltip>
  ) : (
    diskInput
  );

  let displayTitle = label;
  if (isWorker) {
    displayTitle = label.replace(`${service?.app}--`, "");
  }

  return (
    <S.ResourceRow autoscalingEnabled={autoscalingEnabledFlag && hasCapability}>
      <S.ImageAndTitleWrapper indent={isWorker}>
        {isWorker ? (
          <Worker />
        ) : (
          <ServiceIcon service={service} alt={type} title={type} />
        )}
        <S.AppLabelWrapper>
          <Typography.BodyText weight="semiBold" size="medium">
            {displayTitle}
          </Typography.BodyText>

          {thresholdsEnabled && autoscalingEnabledFlag && hasCapability && (
            <Dialog
              title={intl.formatMessage({
                id: "autoscaling.instances.badge.dialog.title"
              })}
              description={intl.formatMessage({
                id: "autoscaling.instances.badge.dialog.description"
              })}
              trigger={
                <div>
                  <AutoscalingIcon />
                </div>
              }
              link={
                <Button variant="outline" onClick={handleConfigureAutoscaling}>
                  {intl.formatMessage({
                    id: "configure.thresholds"
                  })}
                </Button>
              }
            />
          )}
        </S.AppLabelWrapper>
      </S.ImageAndTitleWrapper>

      {/* TYPE */}
      {typeSelectComponent}

      {/* RAM & CPU */}
      <S.SelectWrapper>
        <S.MobileLabel textStyle="default">
          {intl.formatMessage({
            id: `configure_resources.page.cpu_n_ram`,
            defaultMessage: "CPU & RAM"
          })}
        </S.MobileLabel>
        <Select
          // Keep key prop - will help component rerender when values change (Ex: type column)
          key={`configure_resources.page.cpu_ram.${cpuRamValue?.label}`}
          placeholder={
            <S.PlaceholderText weight="regular" size="medium">
              {intl.formatMessage({
                id: "configure_resources.page.cpu_ram.placeholder",
                defaultMessage: "Select CPU and RAM"
              })}
            </S.PlaceholderText>
          }
          id="configure_resources.page.cpu_ram.placeholder"
          value={cpuRamValue.label ? cpuRamValue : undefined}
          options={cpuRamOptions}
          onChange={cpuRamOnChange}
          isOptionDisabled={option => !!option.disabled}
          isDisabled={formIsDisabled}
        />
        {cpuRamError && (
          <S.ErrorText>
            {intl.formatMessage({
              id: `configure_resources.page.cpu_n_ram.error`,
              defaultMessage: "CPU and RAM is required"
            })}
          </S.ErrorText>
        )}
      </S.SelectWrapper>

      {/* INSTANCES */}
      <S.SelectWrapper>
        <S.MobileLabel textStyle="default">
          {intl.formatMessage({
            id: `configure_resources.page.instances`,
            defaultMessage: "Instances"
          })}
        </S.MobileLabel>
        {instancesOnlyText ? (
          <S.NoFieldText weight="regular" size="medium">
            {instancesOnlyText}
          </S.NoFieldText>
        ) : thresholdsEnabled ? (
          <Tooltip
            tooltip={intl.formatMessage({
              id: `disable.autoscaling.suggestion.tooltip`
            })}
          >
            <Select
              value={instancesValue}
              options={instancesOptions}
              onChange={option => {
                instancesOnChange(option as Option);
              }}
              isMulti={false}
              isOptionDisabled={option => !!option.disabled}
              isDisabled={true}
              id="configure_resources.page.instances"
            />
          </Tooltip>
        ) : (
          <Select
            value={instancesValue}
            options={instancesOptions}
            onChange={option => {
              instancesOnChange(option as Option);
            }}
            isMulti={false}
            isOptionDisabled={option => !!option.disabled}
            isDisabled={formIsDisabled}
            id="configure_resources.page.instances"
          />
        )}
      </S.SelectWrapper>

      {/* DISK */}
      {diskOnlyText ? (
        <S.SelectWrapper>
          <S.MobileLabel textStyle="default">{diskLabel}</S.MobileLabel>
          <S.NoFieldText weight="regular" size="medium">
            {diskOnlyText}
          </S.NoFieldText>
        </S.SelectWrapper>
      ) : (
        <S.DiskWrapper>{diskComponent}</S.DiskWrapper>
      )}

      {/* AUTOSCALING */}
      {service &&
      resourceType === "webapps" &&
      autoscalingEnabledFlag &&
      hasCapability ? (
        <S.SelectWrapper>
          <S.AutoscalingBtn variant="link" onClick={handleConfigureAutoscaling}>
            {thresholdsEnabled ? (
              <FormattedMessage id="autoscaling.configure" />
            ) : (
              <FormattedMessage id="autoscaling.enable" />
            )}
          </S.AutoscalingBtn>
        </S.SelectWrapper>
      ) : null}
    </S.ResourceRow>
  );
};

export default ResourceRow;
